import removeMd from "remove-markdown";
import type { Message } from "@/components/feature/layout/types";
import { FileItem, FolderItem } from "@/types/fse";
import { Evidence } from "@/types/evidences";
import { Artefact, StreamMessage } from "@/modules/chat/chat-ui.types";

const PAIRED_ARTEFACT_REGEX = /<artefact\s+([^>]*?)>(?:\s*\n)?([\s\S]*?)(?:\n\s*)?<\/artefact>/g;
const SELF_CLOSING_ARTEFACT_REGEX = /<artefact\s+([^>]*?)\/>/g;
const UNCLOSED_ARTEFACT_REGEX = /<artefact\s+([^>]*?)>(?:\s*\n)?([\s\S]*?)$/g;
const ARTEFACT_ID_REGEX = /id="([^"]*)"/;
const ARTEFACT_NAME_REGEX = /name="([^"]*)"/;
const ARTEFACT_TYPE_REGEX = /type="([^"]*)"/;

export function reformatCitations(text: string): string {
  return text.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (_, p1: string) =>
    p1
      .split(",")
      .map(num => `[${num.trim()}]`)
      .join(""),
  );
}

export function removeCitations(text: string): string {
  const uuidRegex = /\[([\w\-\s,]+)\]/g;
  const ulidPattern =
    /^([0-9A-HJKMNP-TV-Z]{26}|[0-9A-HJKMNP-TV-Z]{26}-[0-9A-HJKMNP-TV-Z]{26}-[0-9A-HJKMNP-TV-Z]{26}-\d+-\d+)$/;

  return text
    .replace(/\[(?:Id:)?(\d+(?:,\s*(?:Id:)?\d+)*)\]/g, "")
    .replace(ulidPattern, "")
    .replace(uuidRegex, "");
}

export function extractFilesFromFolder(folder: FolderItem): FileItem[] {
  const files: FileItem[] = [];
  for (const item of Object.values(folder.items)) {
    if (item.type === "file") {
      files.push(item);
    } else {
      files.push(...extractFilesFromFolder(item));
    }
  }
  return files;
}

export function findFirstFileId(item: FileItem | FolderItem): string | null {
  if (item.type === "file") return item.id;
  for (const child of Object.values(item.items)) {
    const fileId = findFirstFileId(child);
    if (fileId) return fileId;
  }
  return null;
}

function extractItemList(rootItem: FileItem | FolderItem, result: Record<string, string> = {}): Record<string, string> {
  if (rootItem.type === "folder" && typeof rootItem.items !== "undefined") {
    Object.entries(rootItem.items).forEach(([, item]) => {
      result[item.id] = item.name;
      if (item.type === "folder") {
        return extractItemList(item, result);
      }
    });
  } else {
    result[rootItem.id] = rootItem.name;
  }
  return result;
}

function removeDuplicateParentheses(text: string): string {
  return text.replace(/\(([^)]+)\)\s*\(\1\)/g, "($1)");
}

export function extractCitation(message: Message, rootItem: FileItem | FolderItem, evidences: Evidence[]): string {
  const items = extractItemList(rootItem);

  const replaceCitation = (match: string) => {
    const citationIds = match.slice(1, -1).split(",");

    const citation = evidences
      .filter(evidence => citationIds.includes(evidence.citationId))
      .map(evidence => {
        const document = items[evidence.fseId];

        if (!evidence.highlights.length) {
          return "";
        }
        const page = evidence.highlights[0].page + 1;

        return `p.${page}, ${document || ""}`;
      })
      .filter(Boolean);

    const deduplicatedCitation = [...new Set(citation)];

    return ` (${deduplicatedCitation.join(", ")})`;
  };

  const result = message.content
    .replace(/\[\d+(,\d+)*\]/g, replaceCitation)
    .replace(/\[([\w\-\s,]+)\]/g, replaceCitation);

  return removeDuplicateParentheses(result);
}

export function removeMarkdown(text: string): string {
  return removeMd(text);
}

interface ArtefactAttributes {
  id: string;
  name: string;
  type?: string;
}

function extractAttributes(attributesString: string): ArtefactAttributes {
  const idMatch = attributesString.match(ARTEFACT_ID_REGEX);
  const nameMatch = attributesString.match(ARTEFACT_NAME_REGEX);
  const typeMatch = attributesString.match(ARTEFACT_TYPE_REGEX);
  return {
    id: idMatch ? idMatch[1] : "",
    name: nameMatch ? nameMatch[1] : "",
    type: typeMatch ? typeMatch[1] : undefined,
  };
}

function addArtefactAndGetReplacement(
  artefacts: Artefact[],
  artefactId: string,
  artefactName: string,
  content: string,
  isUnclosed: boolean,
  artefactType?: string,
): string {
  const displayName = artefactName || artefactId;
  artefacts.push({ id: artefactId, name: displayName, content, type: artefactType });

  return isUnclosed ? `Artefact processing: ${displayName}` : `[Artefact: ${displayName}](artefact://${artefactId})`;
}

export function extractArtefactsFromString(content: string): [string, Artefact[]] {
  const tempMessage: StreamMessage = { id: "temp", content, type: "ai", isUser: false };
  const [processed, artefacts] = extractArtefacts([tempMessage]);
  return [processed[0].content, artefacts];
}

export function extractArtefacts(messages: StreamMessage[]): [StreamMessage[], Artefact[]] {
  const newMessages: StreamMessage[] = [];
  const artefacts: Artefact[] = [];

  for (const message of messages) {
    let processedContent = message.content;
    let match;
    let hadPairedArtefacts = false;
    let hadSelfClosingArtefacts = false;

    const pairedRegex = new RegExp(PAIRED_ARTEFACT_REGEX.source, "g");
    while ((match = pairedRegex.exec(message.content)) !== null) {
      hadPairedArtefacts = true;
      const [fullMatch, attributesString, artefactContent] = match;
      const { id: artefactId, name: artefactName, type: artefactType } = extractAttributes(attributesString);
      const replacement = addArtefactAndGetReplacement(
        artefacts,
        artefactId,
        artefactName,
        artefactContent.trim(),
        false,
        artefactType,
      );
      processedContent = processedContent.replace(fullMatch, replacement);
    }

    const selfClosingRegex = new RegExp(SELF_CLOSING_ARTEFACT_REGEX.source, "g");
    while ((match = selfClosingRegex.exec(message.content)) !== null) {
      hadSelfClosingArtefacts = true;
      const [fullMatch, attributesString] = match;
      const { id: artefactId, name: artefactName, type: artefactType } = extractAttributes(attributesString);
      const replacement = addArtefactAndGetReplacement(artefacts, artefactId, artefactName, "", false, artefactType);
      processedContent = processedContent.replace(fullMatch, replacement);
    }

    if (!hadPairedArtefacts && !hadSelfClosingArtefacts) {
      const unclosedRegex = new RegExp(UNCLOSED_ARTEFACT_REGEX.source, "g");
      while ((match = unclosedRegex.exec(message.content)) !== null) {
        const [fullMatch, attributesString, artefactContent] = match;
        const { id: artefactId, name: artefactName, type: artefactType } = extractAttributes(attributesString);
        const replacement = addArtefactAndGetReplacement(
          artefacts,
          artefactId,
          artefactName,
          artefactContent.trim(),
          true,
          artefactType,
        );
        processedContent = processedContent.replace(fullMatch, replacement);
      }
    }

    newMessages.push({ ...message, content: processedContent });
  }

  return [newMessages, artefacts];
}

/** Starts a new turn at each message that `startsTurn` accepts. Earlier messages form their own turn. */
export const groupIntoTurns = <T>(messages: T[], startsTurn: (message: T) => boolean): T[][] => {
  const turns: T[][] = [];

  for (const message of messages) {
    const currentTurn = turns.at(-1);
    if (startsTurn(message) || !currentTurn) {
      turns.push([message]);
    } else {
      currentTurn.push(message);
    }
  }

  return turns;
};
